import { useState, useRef } from 'react';
import { Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';

export interface ImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: string[];
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  title?: string;
  description?: string;
  acceptFormats?: string;
  onProcess: (fileContent: string) => Promise<ImportResult>;
}

export function ImportDialog({
  open,
  onClose,
  onImported,
  title = 'Import from CSV',
  description = 'Supports CSV export format',
  acceptFormats = '.csv',
  onProcess,
}: ImportDialogProps) {
  const [fileContent, setFileContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFileContent(reader.result as string);
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFileContent(reader.result as string);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!fileContent) return;
    setImporting(true);
    try {
      const res = await onProcess(fileContent);
      setResult(res);
      if (res.created > 0) onImported();
    } catch (err) {
      setResult({ total: 0, created: 0, skipped: 0, errors: [(err as Error).message] });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFileContent('');
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-[520px] max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <h2 className="text-lg font-semibold text-text">{title}</h2>
          <button onClick={handleClose} className="text-subtle hover:text-muted"><X size={20} /></button>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-line rounded-xl p-8 text-center cursor-pointer
                  hover:border-coral-300 hover:bg-coral-50/30 transition-colors"
              >
                <Upload className="mx-auto text-subtle mb-3" size={32} />
                <p className="text-sm font-medium text-muted">
                  {fileContent ? 'File loaded — click to replace' : 'Drop file here or click to browse'}
                </p>
                <p className="text-xs text-subtle mt-1">{description}</p>
                <input ref={fileRef} type="file" accept={acceptFormats} className="hidden" onChange={handleFile} />
              </div>

              {fileContent && (
                <div className="mt-4">
                  <div className="text-xs text-muted mb-2">
                    {fileContent.split('\n').length - 1} rows detected
                  </div>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="w-full px-4 py-2.5 bg-coral-500 text-white rounded-xl font-medium text-sm
                      hover:bg-coral-600 transition-colors disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              {result.created > 0 ? (
                <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
              ) : (
                <AlertCircle className="mx-auto text-amber-500 mb-3" size={40} />
              )}
              <div className="text-lg font-semibold text-text mb-1">
                {result.created} of {result.total} items imported
              </div>
              {result.skipped > 0 && (
                <div className="text-sm text-muted">{result.skipped} skipped</div>
              )}
              {result.errors.length > 0 && (
                <div className="mt-3 text-left bg-red-50 rounded-lg p-3 max-h-32 overflow-auto">
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-xs text-red-600">{err}</div>
                  ))}
                </div>
              )}
              <button
                onClick={handleClose}
                className="mt-4 px-4 py-2 bg-surface-subtle text-text rounded-xl font-medium text-sm hover:bg-surface-subtle"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
